import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { EditableNumber } from './EditableNumber';
interface OutputEstimatorProps {
  adSpend: number;
  cpaLow: number;
  cpaHigh: number;
  onAdSpendChange: (next: number) => void;
  onCpaLowChange: (next: number) => void;
  onCpaHighChange: (next: number) => void;
  hasLeader?: boolean;
}
const fmt = (value: number) => value.toLocaleString('en-US');
export function OutputEstimator({
  adSpend,
  cpaLow,
  cpaHigh,
  onAdSpendChange,
  onCpaLowChange,
  onCpaHighChange,
  hasLeader = false
}: OutputEstimatorProps) {
  const [isEditingTarget, setIsEditingTarget] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const midCpa = (cpaLow + cpaHigh) / 2;
  const rangeLow = Math.max(1, Math.round(adSpend / cpaHigh));
  const rangeHigh = Math.max(rangeLow + 1, Math.round(adSpend / cpaLow));
  useEffect(() => {
    if (isEditingTarget) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditingTarget]);
  const startEditingTarget = () => {
    setDraft(String(Math.round(adSpend / midCpa)));
    setIsEditingTarget(true);
  };
  const commitTarget = () => {
    const parsed = Number(draft.replace(/[^0-9]/g, ''));
    if (Number.isFinite(parsed) && parsed > 0) {
      const hires = Math.min(500, Math.max(1, parsed));
      onAdSpendChange(Math.round(hires * midCpa));
    }
    setIsEditingTarget(false);
  };
  const stepSpend = (direction: -1 | 1) => {
    const next = direction === 1 ? Math.floor(adSpend / 2500) * 2500 + 2500 : Math.ceil(adSpend / 2500) * 2500 - 2500;
    onAdSpendChange(Math.min(250000, Math.max(2500, next)));
  };
  const arrowButton = 'w-11 h-11 shrink-0 rounded-full flex items-center justify-center text-[#7FE0C3] opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity duration-150 ease-out hover:bg-[#7FE0C3]/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3]';
  const decreaseButton = <button type="button" onClick={() => stepSpend(-1)} aria-label="Decrease monthly ad spend by $2,500" className={arrowButton} data-id="element-624" data-mp="1n4t924chbu2x">
      <ChevronLeftIcon className="w-6 h-6" data-id="element-625" data-mp="1870vhhqhtfrd" />
    </button>;
  const increaseButton = <button type="button" onClick={() => stepSpend(1)} aria-label="Increase monthly ad spend by $2,500" className={arrowButton} data-id="element-626" data-mp="1d5c5bsgsfq9l">
      <ChevronRightIcon className="w-6 h-6" data-id="element-627" data-mp="145p5hb3pisin" />
    </button>;
  return <div className="relative group w-full" data-id="element-628" data-mp="147s3oif0jryg">
      <div className="rounded-2xl border border-[#7FE0C3]/40 bg-[#7FE0C3]/5 px-8 md:px-20 py-16 md:py-24 text-center" data-id="element-629" data-mp="phucdtlsem51">
        <div className="text-sm font-bold uppercase tracking-widest text-[#7FE0C3] mb-8" data-id="element-630" data-mp="ugxhtf5oiudj">
          {hasLeader ? 'Average time frame to find a leader' : 'Average Monthly Output'}
        </div>

        {hasLeader ? <div className="flex items-baseline justify-center gap-3" data-id="element-631" data-mp="1tnz4g1lojd4">
            <span className="text-8xl md:text-9xl font-bold text-white tracking-tight" data-id="element-632" data-mp="1txrube154mi">
              2–8
            </span>
            <span className="text-3xl md:text-4xl font-semibold text-[#7FE0C3]" data-id="element-633" data-mp="3t2biiqga5wi1">
              weeks
            </span>
          </div> : isEditingTarget ? <div className="flex items-center justify-center gap-4" data-id="element-634" data-mp="5tpt0313g8hu">
            {decreaseButton}
            <input ref={inputRef} type="text" inputMode="numeric" value={draft} aria-label="Target hires per month" onChange={event => setDraft(event.target.value)} onBlur={commitTarget} onKeyDown={event => {
          if (event.key === 'Enter') {
            commitTarget();
          }
          if (event.key === 'Escape') {
            setIsEditingTarget(false);
          }
        }} className="w-[5ch] bg-transparent border-b-2 border-[#7FE0C3] text-center text-8xl md:text-9xl font-bold text-white tracking-tight outline-none" data-id="element-635" data-mp="kqyzk3yyxiu5" />
            <span className="text-3xl md:text-4xl font-semibold text-[#7FE0C3]" data-id="element-636" data-mp="1mljrm8q20go3">
              hires / month
            </span>
            {increaseButton}
          </div> : <div className="flex items-center justify-center gap-4" data-id="element-637" data-mp="1wco85cvdo9l1">
            {decreaseButton}
            <button type="button" onClick={startEditingTarget} aria-label={`Average monthly output, ${rangeLow} to ${rangeHigh} hires. Click to enter a target.`} className="flex items-baseline gap-3 rounded-lg px-2 border-b-2 border-dashed border-[#7FE0C3]/40 hover:border-[#7FE0C3] transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3]" data-id="element-638" data-mp="zorrk7zxxe9t">
              <span className="text-8xl md:text-9xl font-bold text-white tracking-tight" data-id="element-639" data-mp="2p1bwxifc5h3c">
                {rangeLow}–{rangeHigh}
              </span>
              <span className="text-3xl md:text-4xl font-semibold text-[#7FE0C3]" data-id="element-640" data-mp="dguko2y12vs6">
                hires / month
              </span>
            </button>
            {increaseButton}
          </div>}

        <p className="mt-4 text-sm text-slate-500" data-id="element-641" data-mp="3mlxlurrnv1e">
          {hasLeader ? 'Average time frame to find a leader is 2 to 8 weeks.' : 'Click any number to set it, or use the arrows to move in $2,500 steps.'}
        </p>

        <div className="mt-12 pt-9 border-t border-[#7FE0C3]/20 grid grid-cols-1 sm:grid-cols-2 gap-9 text-left" data-id="element-642" data-mp="3lubl6a65w6yt">
          <div data-id="element-643" data-mp="3gw9v35abj7du">
            <div className="text-sm font-bold uppercase tracking-widest text-[#7FE0C3] mb-2" data-id="element-644" data-mp="3gvqnoiiao1e2">
              Recommended Monthly Ad Spend
            </div>
            <div className="text-4xl font-bold text-white" data-id="element-645" data-mp="1f4n7zetjrwmn">
              <EditableNumber value={adSpend} onChange={onAdSpendChange} min={500} max={250000} label="Recommended monthly ad spend" prefix="$" className="text-4xl font-bold text-white" data-id="element-646" data-mp="1f1rcry933m1w" />
              <span className="ml-2 text-lg font-medium text-slate-500" data-id="element-647" data-mp="2un34jg2avj93">
                / month
              </span>
            </div>
            <p className="mt-2 text-base text-white" data-id="element-648" data-mp="2zn7kn4zaqbys">
              (separate from monthly service)
            </p>
          </div>

          <div className="sm:text-right" data-id="element-649" data-mp="3ncjpxtri5zvt">
            <div className="text-sm font-bold uppercase tracking-widest text-[#7FE0C3] mb-2" data-id="element-650" data-mp="3salcwps1ujck">
              Cost Per Hire Range
            </div>
            <div className="text-4xl font-bold text-white" data-id="element-651" data-mp="3eaiu91bx3lw9">
              <EditableNumber value={cpaLow} onChange={next => onCpaLowChange(Math.min(next, cpaHigh - 25))} min={25} max={cpaHigh - 25} label="Low end cost per hire" prefix="$" className="text-4xl font-bold text-white" data-id="element-652" data-mp="3ic4dd4f1owew" />
              <span className="mx-2 text-slate-500" data-id="element-653" data-mp="1zc4xrs2485w8">–</span>
              <EditableNumber value={cpaHigh} onChange={next => onCpaHighChange(Math.max(next, cpaLow + 25))} min={cpaLow + 25} max={5000} label="High end cost per hire" prefix="$" className="text-4xl font-bold text-white" data-id="element-654" data-mp="1ufoa0yytvtjo" />
            </div>
            <p className="mt-2 text-base text-slate-500" data-id="element-655" data-mp="1bonao9tspvzl">
              Typed hire targets use the ${fmt(Math.round(midCpa))} midpoint.
            </p>
          </div>
        </div>

        <div className="mt-10 pt-7 border-t border-[#7FE0C3]/20" data-id="element-656" data-mp="1qf4bps2iqjvy">
          <p className="rounded-xl bg-[#7FE0C3]/10 border border-[#7FE0C3]/30 px-6 py-4 text-[#7FE0C3] text-xl md:text-2xl font-semibold leading-snug" data-id="element-657" data-mp="1ll02t39p01s8">
            Ad spend is paid directly to the job platforms and is separate from
            D2D HIRE's fees.
          </p>
        </div>
      </div>
    </div>;
}